import { useState, useEffect } from 'react';
import logger from '../lib/logger';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  getSpendingTrends,
  getCategoryBreakdown,
  getBudgetVsActual,
  getTopMerchants,
  getSpendingByDay
} from '../lib/api';
import { toast } from 'sonner';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  BarChart3,
  PieChart,
  Target,
  ShoppingBag,
  Loader2,
  ArrowUpRight,
  ArrowDownRight,
  AlertTriangle,
  CheckCircle2,
  Calendar,
  Store
} from 'lucide-react';
import { motion } from 'framer-motion';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartPie,
  Pie,
  Cell,
  Legend
} from 'recharts';

const CATEGORY_COLORS = {
  'Shopping': '#10b981',
  'Dining': '#f59e0b',
  'Transport': '#3b82f6',
  'Entertainment': '#8b5cf6',
  'Utilities': '#ef4444',
  'Healthcare': '#ec4899',
  'Travel': '#06b6d4',
  'Other': '#6b7280'
};

const Insights = () => {
  const [loading, setLoading] = useState(true);
  const [trends, setTrends] = useState(null);
  const [categoryData, setCategoryData] = useState(null);
  const [budgetData, setBudgetData] = useState(null);
  const [merchants, setMerchants] = useState(null);
  const [dailySpending, setDailySpending] = useState(null);
  const [period, setPeriod] = useState('month');

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    fetchCategoryData();
  }, [period]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [trendsRes, categoryRes, budgetRes, merchantsRes, dailyRes] = await Promise.all([
        getSpendingTrends(6),
        getCategoryBreakdown('month'),
        getBudgetVsActual(),
        getTopMerchants(5),
        getSpendingByDay(30)
      ]);
      setTrends(trendsRes.data);
      setCategoryData(categoryRes.data);
      setBudgetData(budgetRes.data);
      setMerchants(merchantsRes.data);
      setDailySpending(dailyRes.data);
    } catch (error) {
      logger.error('Error fetching insights:', error);
      toast.error('Failed to load insights');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategoryData = async () => {
    try {
      const res = await getCategoryBreakdown(period);
      setCategoryData(res.data);
    } catch (error) {
      logger.error('Error fetching category data:', error);
    }
  };

  const getTrendIcon = (trend) => {
    if (trend === 'up') return <TrendingUp className="w-4 h-4 text-red-500" />;
    if (trend === 'down') return <TrendingDown className="w-4 h-4 text-emerald-500" />;
    return <Minus className="w-4 h-4 text-muted-foreground" />;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      </Layout>
    );
  }

  const pieData = categoryData?.categories?.map(cat => ({
    name: cat.category,
    value: cat.amount,
    percentage: cat.percentage
  })) || [];

  return (
    <Layout>
      <motion.div
        className="space-y-6 lg:space-y-8"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        data-testid="insights-container"
      >
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold">Spending Insights</h1>
            <p className="text-muted-foreground text-sm sm:text-base">Understand your spending patterns</p>
          </div>
          <Button onClick={fetchAllData} variant="outline" className="rounded-xl w-full sm:w-auto" data-testid="refresh-insights-btn">
            <BarChart3 className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Summary Cards */}
        <motion.div variants={itemVariants} className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <Card data-testid="total-spent-card">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs sm:text-sm text-muted-foreground">Total Spent (6mo)</p>
                {getTrendIcon(trends?.trend)}
              </div>
              <p className="text-xl sm:text-2xl font-heading font-bold">${trends?.total_spent?.toFixed(2) || '0.00'}</p>
              <p className={`text-xs mt-1 ${trends?.change_percent >= 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                {trends?.change_percent >= 0 ? '+' : ''}{trends?.change_percent || 0}% vs last month
              </p>
            </CardContent>
          </Card>

          <Card data-testid="avg-monthly-card">
            <CardContent className="p-4 sm:p-6">
              <p className="text-xs sm:text-sm text-muted-foreground mb-2">Avg Monthly</p>
              <p className="text-xl sm:text-2xl font-heading font-bold">${trends?.average_monthly?.toFixed(2) || '0.00'}</p>
              <p className="text-xs text-muted-foreground mt-1">per month average</p>
            </CardContent>
          </Card>

          <Card data-testid="budget-status-card">
            <CardContent className="p-4 sm:p-6">
              <p className="text-xs sm:text-sm text-muted-foreground mb-2">Budget Status</p>
              <div className="flex items-center gap-2">
                {budgetData?.summary?.overall_status === 'under' ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                )}
                <span className={`text-lg sm:text-xl font-bold ${budgetData?.summary?.overall_status === 'under' ? 'text-emerald-500' : 'text-red-500'}`}>
                  {budgetData?.summary?.overall_status === 'under' ? 'On Track' : 'Over Budget'}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                ${Math.abs(budgetData?.summary?.total_variance || 0).toFixed(2)} {budgetData?.summary?.overall_status === 'under' ? 'remaining' : 'over'}
              </p>
            </CardContent>
          </Card>

          <Card data-testid="top-category-card">
            <CardContent className="p-4 sm:p-6">
              <p className="text-xs sm:text-sm text-muted-foreground mb-2">Top Category</p>
              <p className="text-lg sm:text-xl font-heading font-bold">{categoryData?.top_category || 'N/A'}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {categoryData?.categories?.[0]?.percentage || 0}% of spending
              </p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Main Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Spending Trends Chart */}
          <motion.div variants={itemVariants}>
            <Card>
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-emerald-500" />
                  Monthly Spending Trends
                </CardTitle>
                <CardDescription>Your spending over the last 6 months</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64 sm:h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={trends?.monthly_data || []}>
                      <defs>
                        <linearGradient id="colorSpent" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                      <XAxis dataKey="month_short" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${v}`} />
                      <Tooltip
                        contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                        formatter={(value) => [`$${value.toFixed(2)}`, 'Spent']}
                      />
                      <Area
                        type="monotone"
                        dataKey="total_spent"
                        stroke="#10b981"
                        strokeWidth={2}
                        fillOpacity={1}
                        fill="url(#colorSpent)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Category Breakdown */}
          <motion.div variants={itemVariants}>
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <PieChart className="w-5 h-5 text-blue-500" />
                      Category Breakdown
                    </CardTitle>
                    <CardDescription>Where your money goes</CardDescription>
                  </div>
                  <Select value={period} onValueChange={setPeriod}>
                    <SelectTrigger className="w-full sm:w-32 rounded-xl" data-testid="period-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="week">Week</SelectItem>
                      <SelectItem value="month">Month</SelectItem>
                      <SelectItem value="quarter">Quarter</SelectItem>
                      <SelectItem value="year">Year</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                {pieData.length > 0 ? (
                  <div className="h-64 sm:h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartPie>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={CATEGORY_COLORS[entry.name] || '#6b7280'} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                          formatter={(value) => [`$${value.toFixed(2)}`, 'Spent']}
                        />
                        <Legend />
                      </RechartPie>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-64 flex items-center justify-center text-muted-foreground">
                    <p>No spending data available</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Budget vs Actual */}
        <motion.div variants={itemVariants}>
          <Card>
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Target className="w-5 h-5 text-purple-500" />
                Budget vs Actual
              </CardTitle>
              <CardDescription>Compare your budgets to actual spending</CardDescription>
            </CardHeader>
            <CardContent>
              {budgetData?.cards?.length > 0 ? (
                <div className="space-y-6">
                  {/* Summary Bar */}
                  <div className="p-4 rounded-xl bg-muted">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-3">
                      <div>
                        <p className="text-sm text-muted-foreground">Total Budget Utilization</p>
                        <p className="text-2xl font-bold">
                          ${budgetData.summary.total_actual.toFixed(2)} 
                          <span className="text-sm font-normal text-muted-foreground"> / ${budgetData.summary.total_budget.toFixed(2)}</span>
                        </p>
                      </div>
                      <div className="flex gap-4">
                        <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 border-0">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          {budgetData.summary.cards_under_budget} Under
                        </Badge>
                        <Badge variant="outline" className="bg-red-500/10 text-red-600 border-0">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          {budgetData.summary.cards_over_budget} Over
                        </Badge>
                      </div>
                    </div>
                    <Progress 
                      value={Math.min((budgetData.summary.total_actual / budgetData.summary.total_budget) * 100, 100)} 
                      className="h-3"
                    />
                  </div>

                  {/* Individual Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {budgetData.cards.map((card) => (
                      <div key={card.card_id} className="p-4 rounded-xl border bg-card" data-testid={`budget-card-${card.card_id}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center">
                              <ShoppingBag className="w-4 h-4" />
                            </div>
                            <div>
                              <p className="font-medium text-sm">{card.card_number}</p>
                              <p className="text-xs text-muted-foreground capitalize">{card.card_type}</p>
                            </div>
                          </div>
                          <Badge className={card.status === 'under' ? 'bg-emerald-500/20 text-emerald-600 border-0' : 'bg-red-500/20 text-red-600 border-0'}>
                            {card.status === 'under' ? 'On Track' : 'Over'}
                          </Badge>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Spent</span>
                            <span className="font-mono">${card.actual.toFixed(2)} / ${card.budget.toFixed(2)}</span>
                          </div>
                          <Progress value={Math.min(card.utilization, 100)} className="h-2" />
                          <p className={`text-xs ${card.variance >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                            {card.variance >= 0 ? `$${card.variance.toFixed(2)} remaining` : `$${Math.abs(card.variance).toFixed(2)} over budget`}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Target className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">No budget data available</p>
                  <p className="text-sm text-muted-foreground mt-1">Set up budgets in your card settings</p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Daily Spending & Top Merchants */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Daily Spending */}
          <motion.div variants={itemVariants}>
            <Card>
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-orange-500" />
                  Daily Spending (30 Days)
                </CardTitle>
                <CardDescription>
                  {dailySpending?.highest_day && (
                    <span>Peak: {dailySpending.highest_day.date} (${dailySpending.highest_day.amount.toFixed(2)})</span>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-48 sm:h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dailySpending?.daily_data || []}>
                      <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 10 }}
                        tickFormatter={(v) => v.slice(5)}
                        interval="preserveStartEnd"
                      />
                      <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${v}`} />
                      <Tooltip
                        contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                        formatter={(value) => [`$${value.toFixed(2)}`, 'Spent']}
                        labelFormatter={(label) => `Date: ${label}`}
                      />
                      <Bar dataKey="amount" fill="#f59e0b" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t">
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground">Total</p>
                    <p className="font-bold">${dailySpending?.total?.toFixed(2) || '0.00'}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground">Daily Avg</p>
                    <p className="font-bold">${dailySpending?.average?.toFixed(2) || '0.00'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Top Merchants */}
          <motion.div variants={itemVariants}>
            <Card>
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Store className="w-5 h-5 text-cyan-500" />
                  Top Merchants
                </CardTitle>
                <CardDescription>Where you shop most frequently</CardDescription>
              </CardHeader>
              <CardContent>
                {merchants?.merchants?.length > 0 ? (
                  <div className="space-y-4">
                    {merchants.merchants.map((merchant, idx) => (
                      <div key={merchant.merchant} className="flex items-center gap-4" data-testid={`merchant-${idx}`}>
                        <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center text-sm font-bold">
                          {idx + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{merchant.merchant}</p>
                          <p className="text-xs text-muted-foreground">{merchant.transaction_count} transactions</p>
                        </div>
                        <div className="text-right">
                          <p className="font-mono font-semibold">${merchant.total_spent.toFixed(2)}</p>
                          <p className="text-xs text-muted-foreground">avg ${merchant.avg_transaction.toFixed(2)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Store className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                    <p className="text-muted-foreground">No merchant data yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </motion.div>
    </Layout>
  );
};

export default Insights;
